import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { BackButton } from "../../components/BackButton";
import { AnswerOption } from "../../components/AnswerOption";
import { QuestionText } from "../../components/QuestionText";
import { displayPhone } from "../../lib/phone";
import type { ConsentChannel } from "../../api/onboarding";

export interface WaitingSample {
  id: string;
  subject: string;
  stem: string;
  options: { id: string; text: string }[];
  correctOptionId: string;
  explanation: string;
}

interface WaitingRoomScreenProps {
  /** The contact the link went to, normalised by the server. */
  sentTo?: string | undefined;
  channel?: ConsentChannel | undefined;
  /**
   * When the server will accept another send — an ISO timestamp it gave us.
   *
   * Absent means no idea, which happens on a refresh, and then the button is
   * live: the server owns the cooldown and will say so if it is too soon. A
   * dead button with no explanation is worse than a request that comes back
   * "wait a moment".
   */
  canResendAt?: string | undefined;
  /** A real question from the bank. Null while it loads, or if it cannot. */
  sample?: WaitingSample | null | undefined;
  onResend?: (() => void) | undefined;
  resending?: boolean | undefined;
  resendMessage?: string | null | undefined;
  onChangeContact?: (() => void) | undefined;
}

const CHANNEL_VERB: Record<ConsentChannel, string> = {
  sms: "texted",
  whatsapp: "messaged on WhatsApp",
  email: "emailed",
};

/**
 * Screen 6 — the consent waiting room.
 *
 * The sample question is **answerable but unscored**. It used to be four list
 * items under the words "try one", so a student did exactly that and nothing
 * happened — an interface promising something it would not do. Tapping now
 * marks the answer and explains it, locally and only locally.
 *
 * Nothing is sent. A pre-consent account must generate no activity (HR-3), so
 * no session is opened, no answer is submitted, and the copy says the score
 * starts after a parent says yes rather than implying this one counted.
 *
 * The question itself comes from the real bank. A hardcoded one would be the
 * first thing a student ever sees from us, and it would be invented.
 */
export function WaitingRoomScreen({
  sentTo = "",
  channel = "sms",
  canResendAt,
  sample = null,
  onResend,
  resending = false,
  resendMessage = null,
  onChangeContact,
}: WaitingRoomScreenProps) {
  const [chosen, setChosen] = useState<string | null>(null);

  const remaining = (): number => {
    if (!canResendAt) return 0;
    const ms = new Date(canResendAt).getTime() - Date.now();
    return Number.isNaN(ms) ? 0 : Math.max(0, Math.ceil(ms / 1000));
  };

  const [secondsLeft, setSecondsLeft] = useState(remaining);

  useEffect(() => {
    setSecondsLeft(remaining());
    const timer = setInterval(() => setSecondsLeft(remaining()), 1000);
    return () => clearInterval(timer);
    // `remaining` closes over `canResendAt`, which is the only input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canResendAt]);

  const answered = chosen !== null;
  const wasCorrect = chosen === sample?.correctOptionId;

  // Email addresses are not phone numbers; only one of them wants formatting.
  const shownContact = channel === "email" ? sentTo : displayPhone(sentTo);

  return (
    <div className="app-column px-5 pb-6 pt-5">
      <BackButton to="/parent-phone" />

      <h1 className="m-0 pt-6 font-[family-name:var(--font-display)] text-[24px] font-bold leading-[1.15]">
        Waiting for your parent's OK
      </h1>
      <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
        We {CHANNEL_VERB[channel]} <span className="tabular text-text">{shownContact}</span> a link.
        This screen updates the moment they approve.
      </p>

      <div className="flex gap-3 pt-4">
        <Button variant="secondary" disabled={secondsLeft > 0 || resending} onClick={onResend}>
          {resending ? "Sending…" : "Send it again"}
        </Button>
        <Button variant="ghost" onClick={onChangeContact}>
          Change contact
        </Button>
      </div>

      {/*
        Why it is disabled, in words. The button carried the countdown inside
        its own label, so it read as a control that simply did not work —
        which is exactly how it was reported.
      */}
      {secondsLeft > 0 ? (
        <p className="m-0 pt-2 text-[12.5px] text-text-muted">
          You can send again in{" "}
          <span className="tabular">0:{String(secondsLeft).padStart(2, "0")}</span> — this stops us
          filling their inbox.
        </p>
      ) : null}

      {resendMessage ? (
        <p role="status" className="m-0 pt-3 text-[12.5px] leading-[1.5] text-text-muted">
          {resendMessage}
        </p>
      ) : null}

      {sample ? (
        <section className="mt-6 flex flex-col gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <div className="flex items-center justify-between">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-text-muted">
              While you wait
            </p>
            <span className="rounded-[var(--radius-chip)] border border-border bg-surface-high px-2 py-1 text-[10px] text-text-muted">
              Doesn't count
            </span>
          </div>
          <p className="m-0 text-[12px] text-text-muted">{sample.subject} · try one</p>
          <QuestionText as="p" className="m-0 text-[15px] leading-[1.5]">
            {sample.stem}
          </QuestionText>

          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {sample.options.map((option, index) => (
              <li key={option.id}>
                <AnswerOption
                  letter={String.fromCharCode(65 + index)}
                  text={option.text}
                  state={
                    !answered
                      ? "idle"
                      : option.id === sample.correctOptionId
                        ? "correct"
                        : option.id === chosen
                          ? "wrong"
                          : "idle"
                  }
                  disabled={answered}
                  onClick={answered ? undefined : () => setChosen(option.id)}
                />
              </li>
            ))}
          </ul>

          {answered ? (
            <div className="flex flex-col gap-2 rounded-[var(--radius-control)] bg-surface-high p-3">
              <p
                className={`m-0 text-[13px] font-bold ${wasCorrect ? "text-positive" : "text-negative"}`}
              >
                {wasCorrect ? "Correct" : "Not quite"}
              </p>
              {sample.explanation ? (
                <QuestionText as="p" className="m-0 text-[13px] leading-[1.55] text-text-muted">
                  {sample.explanation}
                </QuestionText>
              ) : null}
            </div>
          ) : null}

          <p className="m-0 text-[12px] text-text-muted">
            Your real score starts after your parent says yes.
          </p>
        </section>
      ) : null}

      {/* Repeated below the sample question so a student who scrolled down
          to answer does not have to scroll back up to find the resend. */}
      {sample ? (
        <div className="flex items-center justify-between gap-3 pt-5">
          <Button
            variant="secondary"
            className="text-[13px]"
            disabled={secondsLeft > 0 || resending}
            onClick={onResend}
          >
            {resending ? "Sending…" : "Send it again"}
          </Button>
          <Button variant="ghost" className="text-[13px]" onClick={onChangeContact}>
            Change contact
          </Button>
        </div>
      ) : null}

      <p className="mt-auto pt-8 text-center text-[12.5px] leading-[1.55] text-text-muted">
        You can close the app — we'll let you know when you're approved.
      </p>
    </div>
  );
}
